import React, { useContext, useMemo } from 'react';
import { LangContext } from '../../contexts/langContext';
import { VARNA } from '../../static/jyotish_varna';
import { getVarnaInterpretation } from '../../static/jyotish_varna_interpretation';
import { VarnaRadarChart } from './VarnaRadarChart';

const INDICATOR_LABEL_KEYS = {
  moon_D1: 'planet_Moon',
  moon_D9: 'jyotish_moon_d9',
  ascendant_D1: 'jyotish_lagna_label',
  sun_D1: 'planet_Sun',
};

/** Render `**cue**` markers from the interpretation summary as bold. */
const renderRichText = (text) => {
  if (!text || !text.includes('**')) return text;
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <b key={i}>{part.slice(2, -2)}</b>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
};

/** Varna names stay internal: the UI shows only the masked theme + emoji (jyotish_ui.json). */
export function VarnaProfile({ profile }) {
  const { $ui, $jyotish, language } = useContext(LangContext);

  const interpretation = useMemo(() => getVarnaInterpretation(profile, language), [profile, language]);

  if (!profile?.primary || !interpretation.primary) return null;

  const emoji = (varnaId) => (varnaId ? $jyotish(VARNA[varnaId].uiKey, 'emoji') : '');
  const theme = (varnaId) => (varnaId ? VARNA[varnaId].theme[language] || VARNA[varnaId].theme.en : '–');

  const focusRow = (varnaId, { large = false } = {}) => (
    <div className={`${large ? 'x_edu_channel_h1' : ''} _f _fm _fgap04`}>
      <span className={large ? 't12' : undefined}>{emoji(varnaId)}</span>
      <strong>{theme(varnaId)}</strong>
    </div>
  );

  const focusLine = (labelKey, sourceParts, rows) => (
    <div className="_fv _fgap02">
      <span className="_o06">
        {[$ui(labelKey), ...sourceParts].filter(Boolean).join(' · ')}
      </span>
      {rows}
    </div>
  );

  const { levels } = profile;
  const manifestationSources = [...new Set(
    levels.manifestation.flatMap((m) => m.sources.map((source) => $ui(INDICATOR_LABEL_KEYS[`${source}_D1`])))
  )];

  return (
    <div className="astro-summary-cell astro-cell-jyotish _fv _fgap02">
      <div className="_fv _fl _fgap04">
        <div className="astro-header astro-header-j">
          <strong>{$ui('jyotish_varna_profile_label')}</strong>
        </div>

        {focusLine('jyotish_varna_primary', [$ui(INDICATOR_LABEL_KEYS.moon_D1)], focusRow(levels.core, { large: true }))}
        {levels.inner && focusLine('jyotish_varna_inner', [$ui(INDICATOR_LABEL_KEYS.moon_D9)], focusRow(levels.inner))}
        {levels.manifestation.length > 0 && focusLine(
          'jyotish_varna_manifestation',
          manifestationSources,
          levels.manifestation.map((m) => (
            <React.Fragment key={m.sources.join('_')}>{focusRow(m.varna)}</React.Fragment>
          ))
        )}

        <VarnaRadarChart distribution={profile.distribution} profile={profile} />

        <div className="x_item_desc x_item_desc--varna _fv _fgap06">
          {interpretation.paragraphs.map((one) => (
            <span
              key={one.key}
              className={`x_edu_item_desc${one.key === 'summary' ? ' x_edu_item_desc--main' : ''}`}
            >
              {one.title && (
                <>
                  <b>{one.title}</b>
                  {(one.varnas || []).map((id) => (
                    <span key={id} aria-hidden="true"> {emoji(id)}</span>
                  ))}
                  <br />
                </>
              )}
              {one.key === 'summary' ? renderRichText(one.text) : one.text}
            </span>
          ))}
        </div>

        <div className="_fv _fgap02" style={{ alignSelf: 'stretch' }}>
          <span className="_o06">{$ui('jyotish_varna_indicators')}</span>
          <div className="" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '1.2em', rowGap: '0.2em' }}>
            {profile.indicators.map((one) => (
              <React.Fragment key={`${one.source}_${one.chart}`}>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {$ui(INDICATOR_LABEL_KEYS[`${one.source}_${one.chart}`])} · {one.sign ? $ui(one.sign) : '–'}
                </span>
                <span>{emoji(one.varna)} {theme(one.varna)}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
